import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import WhatsAppIdentityErrorAlert from '../WhatsAppIdentityErrorAlert.tsx';
import {
  criarLead,
  formatBrazilianPhone,
  parseApiError,
  validateBrazilianWhatsAppPhone,
  type Lead,
  type ParsedApiError,
} from '../../services/api.ts';

interface CreateLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactPhone: string;
  contactName: string;
  clinicId: number;
  onCreated?: (lead: Lead) => void;
}

const SOURCE_OPTIONS = [
  'Facebook',
  'Instagram',
  'Google',
  'Indicação',
  'Fachada',
  'Site',
  'Orgânico',
  'Convênio',
  'Carteira de Pacientes',
  'Radio',
  'TV',
];

const nowDatetimeLocal = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  isOpen,
  onClose,
  contactPhone,
  contactName,
  clinicId,
  onCreated,
}) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ParsedApiError | null>(null);
  const [form, setForm] = useState({
    name: '',
    source_id: 'Facebook',
    data_entrada: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    setSaveError(null);
    setForm({
      name: contactName || '',
      source_id: 'Facebook',
      data_entrada: nowDatetimeLocal(),
    });
  }, [isOpen, contactName]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, saving]);

  const handleSave = async () => {
    if (saving) return;

    if (!form.name.trim()) {
      setSaveError({ message: 'Nome é obrigatório.' });
      return;
    }

    const phoneValidation = validateBrazilianWhatsAppPhone(contactPhone);
    if (!phoneValidation.isValid) {
      setSaveError({
        message: phoneValidation.error || 'Telefone inválido.',
        code: 'invalid_phone',
      });
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const userType = localStorage.getItem('user_type');
      const clientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : parseInt(localStorage.getItem('client_id') || '0', 10);
      const payload: any = {
        client_id: String(clientId),
        name: form.name.trim(),
        phone: phoneValidation.normalized,
        source_id: form.source_id,
      };
      if (form.data_entrada) {
        payload.data_entrada = form.data_entrada.replace('T', ' ') + ':00';
      }

      const lead = await criarLead(clientId, clinicId, payload);

      onCreated?.(lead);
      onClose();
    } catch (err: unknown) {
      setSaveError(parseApiError(err, 'Erro ao criar lead'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenExistingIdentity = () => {
    if (!saveError) return;

    const phone = saveError.existingPhone || contactPhone;
    onClose();

    if (saveError.existingLeadId) {
      navigate('/crm', {
        state: {
          searchTerm: phone,
          existingLeadId: saveError.existingLeadId,
        },
      });
      return;
    }

    navigate('/contacts', {
      state: {
        searchTerm: phone,
        existingContactId: saveError.existingContactId,
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => {
          if (!saving) onClose();
        }}
      />

      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        role="dialog"
        aria-label="Criar lead"
      >
        <div
          className={`px-5 py-4 border-b flex items-center justify-between flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Criar lead
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Converter este contato em um novo lead
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            title="Fechar (Esc)"
            className={`p-2 rounded-xl transition-colors ${
              isDark
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Nome
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setSaveError(null);
              }}
              className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
              placeholder="Nome completo"
            />
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Telefone
            </label>
            <input
              type="text"
              value={formatBrazilianPhone(contactPhone)}
              disabled
              className={`w-full p-3 border rounded-xl text-sm cursor-not-allowed ${
                isDark
                  ? 'bg-gray-700/50 border-gray-700 text-gray-400'
                  : 'bg-gray-100 border-gray-200 text-gray-500'
              }`}
            />
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Vem do contato — não pode ser alterado.
            </p>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Origem (Mídia)
            </label>
            <select
              value={form.source_id}
              onChange={(e) => setForm({ ...form, source_id: e.target.value })}
              className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Data de entrada
            </label>
            <input
              type="datetime-local"
              value={form.data_entrada}
              onChange={(e) => setForm({ ...form, data_entrada: e.target.value })}
              className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {saveError && (
            <WhatsAppIdentityErrorAlert
              error={saveError}
              onOpenExisting={handleOpenExistingIdentity}
            />
          )}
        </div>

        <div
          className={`px-5 py-4 border-t flex items-center justify-end gap-2 flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            onClick={onClose}
            disabled={saving}
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              isDark
                ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            className="rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white transition-colors flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Verificando WhatsApp…' : 'Criar lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLeadModal;
